import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {JobDetailFormData} from '../../components/ai/review/create-job/new-job-detail/NewJobDetail';
import {JobType} from '../../api/types/aiReview.type';
import {SourceSelectionMode} from '../../components/ai/review/create-job/new-job-detail/source-selection-step/SourceSelectionStep';
import {ToastType} from '../../shared/constants';

interface ToastState {
  open: boolean;
  title: string;
  message: string;
  status: ToastType[];
}

interface EditModeState {
  caseContext: boolean;
  relevanceCriteria: boolean;
  issueCode: boolean;
}
interface AiReviewJobType {
  jobType: JobType;
  formData: JobDetailFormData;
  initialFormData: JobDetailFormData; //to track if form is edited
  cloneJobName: string; //set cloned job name
  toast: ToastState;
  editMode: EditModeState;
  defineCriteria: string;
}

const formDataInitialState: JobDetailFormData = {
  jobDetail: {jobName: '', description: '', selectedGroup: null},
  contextAndCriteria: {
    context: '',
    issueCodes: [],
    relevanceCriteria: '',
  },
  groundTruthTagID: null,
  sourceSelectionMode: 'SAVEDSEARCH' as SourceSelectionMode,
  sourceSelection: 0,
  sourceSelectionName: '',
  totalDocs: null,
  reviewProtocolID: undefined,
  parentProtocolID: undefined,
  isProtocolModified: false,
  parentReviewJobID: undefined,
  isManual: false,
};

const toastInitialState = {
  open: false,
  title: '',
  message: '',
  status: [],
};

const editModeIntitialState = {
  caseContext: false,
  relevanceCriteria: false,
  issueCode: false,
};

const initialState: AiReviewJobType = {
  jobType: 'RELEVANCY',
  formData: formDataInitialState,
  initialFormData: formDataInitialState,
  cloneJobName: '',
  toast: toastInitialState,
  editMode: editModeIntitialState,
  defineCriteria: 'manual',
};

export const AiReviewSlice = createSlice({
  name: 'aiReview',
  initialState,
  reducers: {
    setJobType(state, action: PayloadAction<JobType>) {
      state.jobType = action.payload;
    },
    setFormData(state, action: PayloadAction<Partial<JobDetailFormData>>) {
      state.formData = {
        ...state.formData,
        ...action.payload,
      };
    },
    resetFormData(state) {
      state.formData = formDataInitialState;
      state.initialFormData = formDataInitialState;
    },
    setInitialFormData(state, action: PayloadAction<JobDetailFormData>) {
      state.initialFormData = action.payload;
    },
    setCloneJobName(state, action: PayloadAction<string>) {
      state.cloneJobName = action.payload;
    },
    showToast(
      state,
      action: PayloadAction<{
        title: string;
        message: string;
        status: ToastType[];
      }>
    ) {
      state.toast = {
        open: true,
        title: action.payload.title,
        message: action.payload.message,
        status: action.payload.status,
      };
    },

    hideToast(state) {
      state.toast = toastInitialState;
    },
    setEditMode(state, action: PayloadAction<Partial<EditModeState>>) {
      state.editMode = {...state.editMode, ...action.payload};
    },
    resetEditMode(state) {
      state.editMode = editModeIntitialState;
    },
    setDefineCriteria(state, action: PayloadAction<string>) {
      const prev = state.defineCriteria;
      const next = action.payload;

      // Reset ONLY when switching to SAVED
      if (next === 'saved' && prev !== 'saved') {
        state.editMode = editModeIntitialState;
      }

      state.defineCriteria = next;
    },
  },
});
// Export actions
export const {
  setJobType,
  setFormData,
  resetFormData,
  setInitialFormData,
  setCloneJobName,
  showToast,
  hideToast,
  setEditMode,
  resetEditMode,
  setDefineCriteria,
} = AiReviewSlice.actions;

export default AiReviewSlice.reducer;
